import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LayoutDashboard, Package, Receipt, Palette, LogOut, Plus, Pencil, Trash2, X, TrendingUp, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { fmtPrice, CATEGORY_LABELS } from "../components/Reveal";
import { useStore } from "../context/StoreContext";
import ContentPanel from "./ContentPanel";
import ImageField from "../components/ImageField";
import GalleryField from "../components/GalleryField";

const EMPTY = { name: "", category: "physical", brand: "", availability: "in_stock", tagline: "", description: "", price: 0, unit: "", image: "", gallery: [], featured: false, active: true, specs: [] };

const QUOTE_STATUS = {
  new: { cls: "border-blue text-blue" },
  in_progress: { cls: "border-gold text-gold" },
  won: { cls: "border-green-600 text-green-700" },
  lost: { cls: "border-ink/30 text-ink/40" },
};

function ProductModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name || !form.price) { toast.error("Nombre y precio son obligatorios"); return; }
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price) };
      if (form.id) await api.put(`/admin/products/${form.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success("Producto guardado");
      onSaved();
    } catch { toast.error("Error al guardar"); } finally { setSaving(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-inverse/70 backdrop-blur-sm z-[80] flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()} className="bg-[color:var(--bg)] w-full max-w-lg max-h-[88vh] overflow-y-auto" data-testid="product-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink/10 sticky top-0 bg-[color:var(--bg)]">
          <h3 className="font-display font-extrabold text-xl">{form.id ? "Editar" : "Nuevo"} producto</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Nombre"><input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="pf-name" className="inp" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Categoría">
              <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="pf-category" className="inp">
                <option value="physical">Equipos</option><option value="software">Software</option><option value="consulting">Consultoría</option>
              </select>
            </Field>
            <Field label="Precio (USD)"><input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="pf-price" className="inp" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Etiqueta"><input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} className="inp" /></Field>
            <Field label="Unidad"><input value={form.unit} onChange={(e) => set("unit", e.target.value)} placeholder="unidad / licencia/año" className="inp" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Marca / Fabricante"><input value={form.brand} onChange={(e) => set("brand", e.target.value)} data-testid="pf-brand" placeholder="TerraCore, DrillTech…" className="inp" /></Field>
            <Field label="Disponibilidad">
              <select value={form.availability} onChange={(e) => set("availability", e.target.value)} data-testid="pf-availability" className="inp">
                <option value="in_stock">En stock</option><option value="on_demand">Bajo pedido</option>
              </select>
            </Field>
          </div>
          <Field label="Imagen del producto"><ImageField value={form.image} onChange={(v) => set("image", v)} testid="pf-image" /></Field>
          <Field label="Galería (imágenes adicionales)"><GalleryField value={form.gallery || []} onChange={(v) => set("gallery", v)} testid="pf-gallery" /></Field>
          <Field label="Descripción"><textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={4} className="inp resize-none" /></Field>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="pf-featured" /> Destacado</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} /> Activo</label>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-ink/10 flex justify-end gap-3 sticky bottom-0 bg-[color:var(--bg)]">
          <button onClick={onClose} className="px-5 py-2.5 border border-ink/15 hover:border-gold transition-colors">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="pf-save" className="px-5 py-2.5 bg-blue text-[color:var(--bg)] hover:bg-gold hover:text-inverse transition-colors disabled:opacity-60">{saving ? "Guardando…" : "Guardar"}</button>
        </div>
      </motion.div>
      <style>{`.inp{width:100%;border:1px solid rgba(10,18,26,.15);background:var(--bg);padding:.65rem .8rem;outline:none;font-size:.9rem}.inp:focus{border-color:var(--gold)}`}</style>
    </motion.div>
  );
}

const Field = ({ label, children }) => (<div><label className="block text-xs uppercase tracking-wider text-ink/50 mb-1.5 font-mono">{label}</label>{children}</div>);

function ThemePanel() {
  const { settings, refreshSettings, applyColors } = useStore();
  const [colors, setColors] = useState(settings.colors);
  const [brand, setBrand] = useState(settings.brand_name);

  useEffect(() => { setColors(settings.colors); setBrand(settings.brand_name); }, [settings]);

  const preview = (k, v) => { const next = { ...colors, [k]: v }; setColors(next); applyColors(next); };
  const save = async () => {
    try { await api.put("/admin/settings", { brand_name: brand, colors }); await refreshSettings(); toast.success("Ajustes guardados"); }
    catch { toast.error("Error al guardar"); }
  };
  const reset = () => { const d = { bg: "#FDFDFD", text: "#0A121A", blue: "#0A1F3A", gold: "#D4AF37", surface: "#F4F5F7" }; setColors(d); applyColors(d); };

  const swatches = [["blue", "Azul primario"], ["gold", "Dorado acento"], ["bg", "Fondo"], ["text", "Texto"], ["surface", "Superficie"]];
  return (
    <div className="max-w-xl" data-testid="theme-panel">
      <h2 className="font-display font-extrabold text-2xl mb-1">Identidad & Colores</h2>
      <p className="text-ink/50 text-sm mb-8">Los cambios se previsualizan al instante en todo el sitio.</p>
      <Field label="Nombre de marca"><input value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="brand-input" className="inp mb-6" /></Field>
      <div className="space-y-4">
        {swatches.map(([k, lbl]) => (
          <div key={k} className="flex items-center justify-between border border-ink/10 p-3">
            <span className="font-medium">{lbl}</span>
            <div className="flex items-center gap-3">
              <span className="font-mono text-sm text-ink/50">{colors[k]}</span>
              <input type="color" value={colors[k]} onChange={(e) => preview(k, e.target.value)} data-testid={`color-${k}`} className="w-10 h-10 cursor-pointer border-0 bg-transparent" />
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-8">
        <button onClick={save} data-testid="save-theme" className="px-6 py-3 bg-blue text-[color:var(--bg)] hover:bg-gold hover:text-inverse transition-colors font-medium">Guardar cambios</button>
        <button onClick={reset} className="px-6 py-3 border border-ink/15 hover:border-gold transition-colors">Restablecer</button>
      </div>
      <style>{`.inp{width:100%;border:1px solid rgba(10,18,26,.15);background:var(--bg);padding:.65rem .8rem;outline:none}.inp:focus{border-color:var(--gold)}`}</style>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [modal, setModal] = useState(null);
  const navigate = useNavigate();
  const { setAdmin, settings } = useStore();

  const load = useCallback(async () => {
    const [s, p, o, q] = await Promise.all([api.get("/admin/stats"), api.get("/admin/products"), api.get("/admin/orders"), api.get("/admin/quotes")]);
    setStats(s.data); setProducts(p.data); setOrders(o.data); setQuotes(q.data);
  }, []);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => { setAuthed(true); setAdmin(data); load(); })
      .catch(() => navigate("/login"));
  }, [navigate, load, setAdmin]);

  const logout = async () => { try { await api.post("/auth/logout"); } catch {} localStorage.removeItem("tn_token"); navigate("/login"); };
  const del = async (id) => { if (!window.confirm("¿Eliminar producto?")) return; await api.delete(`/admin/products/${id}`); toast.success("Eliminado"); load(); };

  if (authed === null) return <div className="min-h-screen flex items-center justify-center font-mono text-ink/40">Verificando…</div>;

  const nav = [["overview", "Resumen", LayoutDashboard], ["products", "Productos", Package], ["orders", "Órdenes", Receipt], ["quotes", "Cotizaciones", FileText], ["theme", "Diseño & Contenido", Palette]];

  return (
    <div className="min-h-screen flex bg-surface" data-testid="admin-page">
      <aside className="w-16 lg:w-60 bg-inverse text-[color:var(--bg)] flex flex-col shrink-0 sticky top-0 h-screen">
        <div className="p-5 flex items-center gap-2 border-b border-[color:var(--bg)]/10">
          <span className="w-3 h-3 bg-gold rotate-45 shrink-0" /><span className="font-display font-extrabold hidden lg:block">{settings?.brand_name || "OOMINING"}</span>
        </div>
        <nav className="flex-1 py-4">
          {nav.map(([k, lbl, Icon]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`w-full flex items-center gap-3 px-5 py-3.5 text-sm transition-colors ${tab === k ? "bg-gold text-inverse" : "text-[color:var(--bg)]/60 hover:text-gold"}`}>
              <Icon className="w-4.5 h-4.5 shrink-0" /><span className="hidden lg:block">{lbl}</span>
            </button>
          ))}
        </nav>
        <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-3 px-5 py-4 text-sm text-[color:var(--bg)]/60 hover:text-red-400 border-t border-[color:var(--bg)]/10"><LogOut className="w-4 h-4" /><span className="hidden lg:block">Salir</span></button>
      </aside>

      <main className="flex-1 p-6 md:p-10 overflow-x-hidden">
        {tab === "overview" && stats && (
          <div>
            <h1 className="font-display font-extrabold text-3xl mb-8">Resumen</h1>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[["Productos", stats.total_products], ["Órdenes", stats.total_orders], ["Pagadas", stats.paid_orders], ["Ingresos", fmtPrice(stats.revenue)]].map(([l, v], i) => (
                <div key={i} className="bg-[color:var(--bg)] border border-ink/10 p-6" data-testid={`stat-${i}`}>
                  <p className="overline text-ink/40 mb-3">{l}</p>
                  <p className="font-display font-extrabold text-3xl">{v}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 bg-[color:var(--bg)] border border-ink/10 p-6 flex items-center gap-4">
              <TrendingUp className="w-6 h-6 text-gold" />
              <p className="text-ink/60 text-sm">Gestiona tu catálogo, revisa las órdenes y personaliza la identidad visual desde el menú lateral.</p>
            </div>
          </div>
        )}

        {tab === "products" && (
          <div>
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-display font-extrabold text-3xl">Productos</h1>
              <button onClick={() => setModal(EMPTY)} data-testid="new-product-btn" className="flex items-center gap-2 bg-blue text-[color:var(--bg)] px-5 py-2.5 hover:bg-gold hover:text-inverse transition-colors font-medium"><Plus className="w-4 h-4" /> Nuevo</button>
            </div>
            <div className="bg-[color:var(--bg)] border border-ink/10">
              {products.map((p) => (
                <div key={p.id} className="flex items-center gap-4 p-4 border-b border-ink/10 last:border-0" data-testid={`admin-product-${p.slug}`}>
                  <div className="w-14 h-14 bg-surface overflow-hidden shrink-0"><img src={p.image} alt={p.name} className="w-full h-full object-cover" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{p.name} {!p.active && <span className="text-xs text-ink/40 font-mono">(inactivo)</span>}</p>
                    <p className="text-sm text-ink/50">{CATEGORY_LABELS[p.category]} · {fmtPrice(p.price)}</p>
                  </div>
                  <button onClick={() => setModal(p)} data-testid={`edit-${p.slug}`} className="p-2 hover:text-gold transition-colors"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(p.id)} data-testid={`delete-${p.slug}`} className="p-2 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "orders" && (
          <div>
            <h1 className="font-display font-extrabold text-3xl mb-8">Órdenes</h1>
            <div className="bg-[color:var(--bg)] border border-ink/10 overflow-x-auto">
              {orders.length === 0 ? <p className="p-6 text-ink/40 font-mono text-sm">Aún no hay órdenes.</p> : (
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-ink/10 text-left text-ink/40 font-mono text-xs uppercase">
                    <th className="p-4">Email</th><th className="p-4">Ítems</th><th className="p-4">Monto</th><th className="p-4">Estado</th></tr></thead>
                  <tbody>
                    {orders.map((o) => (
                      <tr key={o._id} className="border-b border-ink/10 last:border-0" data-testid={`order-row`}>
                        <td className="p-4">{o.email || "—"}</td>
                        <td className="p-4 text-ink/60">{(o.items || []).map((i) => `${i.name} ×${i.quantity}`).join(", ")}</td>
                        <td className="p-4 font-medium">{fmtPrice(o.amount || 0)}</td>
                        <td className="p-4"><span className={`px-2.5 py-1 text-xs font-mono ${o.payment_status === "paid" ? "bg-gold/20 text-gold" : "bg-ink/10 text-ink/50"}`}>{o.payment_status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {tab === "quotes" && (
          <div>
            <h1 className="font-display font-extrabold text-3xl mb-8">Cotizaciones</h1>
            <div className="bg-[color:var(--bg)] border border-ink/10 overflow-x-auto">
              {quotes.length === 0 ? <p className="p-6 text-ink/40 font-mono text-sm">Aún no hay solicitudes de cotización.</p> : (
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-ink/10 text-left text-ink/40 font-mono text-xs uppercase">
                    <th className="p-4">Nombre</th><th className="p-4">Empresa</th><th className="p-4">Contacto</th><th className="p-4">Producto</th><th className="p-4">Cant.</th><th className="p-4">Estado</th></tr></thead>
                  <tbody>
                    {quotes.map((q) => (
                      <tr key={q._id} className="border-b border-ink/10 last:border-0 align-top" data-testid="quote-row">
                        <td className="p-4 font-medium">{q.name}</td>
                        <td className="p-4 text-ink/60">{q.company || "—"}</td>
                        <td className="p-4 text-ink/60">{q.email}<br/>{q.phone}</td>
                        <td className="p-4 text-ink/60">{q.product_name || "—"}</td>
                        <td className="p-4">{q.quantity || "—"}</td>
                        <td className="p-4">
                          <select value={q.status || "new"} data-testid={`quote-status-${q._id}`}
                            onChange={async (e) => { await api.patch(`/admin/quotes/${q._id}`, { status: e.target.value }); toast.success("Estado actualizado"); load(); }}
                            className={`px-2 py-1.5 text-xs font-mono border cursor-pointer outline-none ${QUOTE_STATUS[q.status || "new"]?.cls || ""}`}>
                            <option value="new">Nueva</option>
                            <option value="in_progress">En proceso</option>
                            <option value="won">Ganada</option>
                            <option value="lost">Perdida</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {tab === "theme" && <ContentPanel />}
      </main>

      <AnimatePresence>{modal && <ProductModal initial={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}</AnimatePresence>
    </div>
  );
}
